export const DEV_BATTLE_SETUP_SLOT_COUNT = 20;
export const DEV_BATTLE_SETUP_HAND_COUNT = 10;

export const DEV_BATTLE_SETUP_SLOT_LABELS = [
  'Opp Front 1',
  'Opp Front 2',
  'Opp Front 3',
  'Opp Front 4',
  'Opp Front 5',
  'Opp Flank 1',
  'Opp Flank 2',
  'Opp Flank 3',
  'Opp Flank 4',
  'Opp Flank 5',
  'You Front 1',
  'You Front 2',
  'You Front 3',
  'You Front 4',
  'You Front 5',
  'You Flank 1',
  'You Flank 2',
  'You Flank 3',
  'You Flank 4',
  'You Flank 5',
];

/**
 * @param {number} slotIndex 0–19
 * @returns {{ targetPlayer: 'opponent'|'player', zone: 'front'|'flank', index: number }}
 */
export function slotIndexToPlacement(slotIndex) {
  const idx = Number(slotIndex);
  if (!Number.isInteger(idx) || idx < 0 || idx >= DEV_BATTLE_SETUP_SLOT_COUNT) {
    throw new Error(`devBattleSetupSlots: invalid slot index ${slotIndex}`);
  }
  if (idx < 5) {
    return { targetPlayer: 'opponent', zone: 'front', index: idx };
  }
  if (idx < 10) {
    return { targetPlayer: 'opponent', zone: 'flank', index: idx - 5 };
  }
  if (idx < 15) {
    return { targetPlayer: 'player', zone: 'front', index: idx - 10 };
  }
  return { targetPlayer: 'player', zone: 'flank', index: idx - 15 };
}

function readUnitIdAtSlot(playerState, zone, index) {
  const unit = playerState?.playedOrders?.[zone]?.[index];
  if (!unit || unit.id == null) {
    return '';
  }
  return String(unit.id);
}

/**
 * Build form defaults from the live dev battle.
 * @param {object} params
 * @param {object|null} params.gameState
 * @param {string|number|null} params.userId
 * @param {string|null} params.opponentUsername
 * @param {{ showAutowin?: boolean, showDevConsole?: boolean, opponentName?: string, opponentTitleId?: number|null }|null} params.devBattleSetupUi
 */
export function buildDevBattleSetupSnapshot({
  gameState,
  userId,
  opponentUsername,
  devBattleSetupUi,
}) {
  const emptySlots = Array(DEV_BATTLE_SETUP_SLOT_COUNT).fill('');
  const emptyHand = Array(DEV_BATTLE_SETUP_HAND_COUNT).fill('');

  if (!gameState || userId == null) {
    return {
      slots: emptySlots,
      handUnitIds: emptyHand,
      playerIntel: '5',
      playerMaxIntel: '8',
      playerHealth: '30',
      opponentIntel: '5',
      opponentMaxIntel: '8',
      opponentHealth: '30',
      opponentName: opponentUsername ?? 'Bot',
      opponentTitleId: '',
      showAutowin: devBattleSetupUi?.showAutowin !== false,
      showDevConsole: devBattleSetupUi?.showDevConsole !== false,
    };
  }

  const opponentId = gameState.players?.find((id) => String(id) !== String(userId));
  const playerState =
    gameState.state?.[userId] ??
    gameState.state?.[String(userId)] ??
    gameState.state?.[Number(userId)];
  const opponentState =
    opponentId != null
      ? gameState.state?.[opponentId] ??
        gameState.state?.[String(opponentId)] ??
        gameState.state?.[Number(opponentId)]
      : null;

  const slots = [...emptySlots];
  if (opponentState) {
    for (let i = 0; i < 5; i += 1) {
      slots[i] = readUnitIdAtSlot(opponentState, 'front', i);
      slots[5 + i] = readUnitIdAtSlot(opponentState, 'flank', i);
    }
  }
  if (playerState) {
    for (let i = 0; i < 5; i += 1) {
      slots[10 + i] = readUnitIdAtSlot(playerState, 'front', i);
      slots[15 + i] = readUnitIdAtSlot(playerState, 'flank', i);
    }
  }

  const handUnitIds = [...emptyHand];
  if (playerState?.hand) {
    playerState.hand.slice(0, DEV_BATTLE_SETUP_HAND_COUNT).forEach((order, i) => {
      if (order?.id != null) {
        handUnitIds[i] = String(order.id);
      }
    });
  }

  const resolvedOpponentName =
    devBattleSetupUi?.opponentName != null && devBattleSetupUi.opponentName !== ''
      ? devBattleSetupUi.opponentName
      : opponentUsername ?? 'Bot';

  return {
    slots,
    handUnitIds,
    playerIntel: String(playerState?.intelligence ?? 5),
    playerMaxIntel: String(playerState?.maxIntelligence ?? 8),
    playerHealth: String(playerState?.health ?? 30),
    opponentIntel: String(opponentState?.intelligence ?? 5),
    opponentMaxIntel: String(opponentState?.maxIntelligence ?? 8),
    opponentHealth: String(opponentState?.health ?? 30),
    opponentName: resolvedOpponentName,
    opponentTitleId:
      devBattleSetupUi?.opponentTitleId != null && devBattleSetupUi.opponentTitleId !== ''
        ? String(devBattleSetupUi.opponentTitleId)
        : '',
    showAutowin: devBattleSetupUi?.showAutowin !== false,
    showDevConsole: devBattleSetupUi?.showDevConsole !== false,
  };
}

/**
 * @param {object} form
 * @returns {object} play-turn action payload
 */
export function buildDevSetupBattleAction(form) {
  if (!form || !Array.isArray(form.slots) || form.slots.length !== DEV_BATTLE_SETUP_SLOT_COUNT) {
    throw new Error('buildDevSetupBattleAction: slots must be an array of 20 entries');
  }

  const parseStat = (raw, label) => {
    if (raw === '' || raw == null) {
      throw new Error(`buildDevSetupBattleAction: ${label} is required`);
    }
    const n = parseInt(String(raw), 10);
    if (!Number.isFinite(n)) {
      throw new Error(`buildDevSetupBattleAction: invalid ${label}: ${raw}`);
    }
    return n;
  };

  const slots = form.slots.map((raw) => {
    const trimmed = String(raw ?? '').trim();
    if (trimmed === '') {
      return null;
    }
    const id = parseInt(trimmed, 10);
    if (!Number.isFinite(id) || id < 1) {
      throw new Error(`buildDevSetupBattleAction: invalid slot unit id: ${raw}`);
    }
    return id;
  });

  const handUnitIds = (form.handUnitIds || [])
    .map((raw) => String(raw ?? '').trim())
    .filter((trimmed) => trimmed !== '')
    .slice(0, DEV_BATTLE_SETUP_HAND_COUNT)
    .map((trimmed) => {
      const id = parseInt(trimmed, 10);
      if (!Number.isFinite(id) || id < 1) {
        throw new Error(`buildDevSetupBattleAction: invalid hand unit id: ${trimmed}`);
      }
      return id;
    });

  return {
    type: 'devSetupBattle',
    slots,
    handUnitIds,
    playerIntel: parseStat(form.playerIntel, 'playerIntel'),
    playerMaxIntel: parseStat(form.playerMaxIntel, 'playerMaxIntel'),
    playerHealth: parseStat(form.playerHealth, 'playerHealth'),
    opponentIntel: parseStat(form.opponentIntel, 'opponentIntel'),
    opponentMaxIntel: parseStat(form.opponentMaxIntel, 'opponentMaxIntel'),
    opponentHealth: parseStat(form.opponentHealth, 'opponentHealth'),
  };
}
